import { useEffect, useRef, useState } from "preact/hooks";
import { applyOps, summarizeElement } from "@overlay/runtime-patcher";
import type { ChangeSet, EditOp } from "@overlay/op-schema";
import { startPicker } from "./picker";
import { InspectTab } from "./InspectTab";
import { CodeTab } from "./CodeTab";

type Config = {
  project: string;
  env: string;
  apiBase: string;
  fragmentKey: string;
  storageKey: string;
};

type Session = {
  token: string;
  expiresAt: number;
  projectId: string;
  environment: string;
};

type Selection = ReturnType<typeof summarizeElement>;

type PageContext = {
  title?: string;
  pathname?: string;
  bodyPreview?: string;
};

function summarizePage(ignoreHost: HTMLElement | null): PageContext {
  const title = document.title;
  const pathname = location.pathname;
  let bodyPreview = "";
  try {
    const clone = document.body.cloneNode(true) as HTMLElement;
    if (ignoreHost) {
      const match = clone.querySelector(`#${ignoreHost.id}`);
      match?.remove();
    }
    clone.querySelectorAll("script, style, noscript").forEach((n) => n.remove());
    bodyPreview = (clone.innerHTML || "").replace(/\s+/g, " ").slice(0, 6000);
  } catch {
    bodyPreview = (document.body.innerText || "").slice(0, 2000);
  }
  return { title, pathname, bodyPreview };
}

type AssistantStatus =
  | { kind: "loading" }
  | {
      kind: "ready";
      changeSet: ChangeSet;
      revert: () => void;
      applied: boolean;
    }
  | { kind: "error"; message: string }
  | { kind: "discarded"; changeSet: ChangeSet };

type ChatMessage =
  | { id: string; role: "user"; text: string; hadSelection: boolean }
  | { id: string; role: "assistant"; prompt: string; status: AssistantStatus };

type Tab = "chat" | "inspect" | "code";
type Dock = "left" | "right" | "top" | "bottom";

const DOCK_STORAGE_KEY = "overlay_dock";
const ALLOWED_DOCKS: Dock[] = ["left", "right", "top", "bottom"];
function readDock(): Dock {
  try {
    const raw = localStorage.getItem(DOCK_STORAGE_KEY);
    if (raw && (ALLOWED_DOCKS as string[]).includes(raw)) return raw as Dock;
  } catch {}
  return "left";
}

const mkId = () =>
  `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

export function OverlayApp({
  config,
  session,
  signOut,
}: {
  config: Config;
  session: Session;
  signOut: () => void;
}) {
  const [picking, setPicking] = useState(false);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [hoverRect, setHoverRect] = useState<DOMRect | null>(null);
  const [prompt, setPrompt] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [pending, setPending] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [usage, setUsage] = useState<{ used: number; included: number }>({
    used: 0,
    included: 10,
  });
  const [tab, setTab] = useState<Tab>("chat");
  const [collapsed, setCollapsed] = useState(false);
  const [dock, setDock] = useState<Dock>(readDock);
  useEffect(() => {
    try { localStorage.setItem(DOCK_STORAGE_KEY, dock); } catch {}
  }, [dock]);
  const [saveStatus, setSaveStatus] = useState<
    | { kind: "idle" }
    | { kind: "saving" }
    | { kind: "saved"; at: number }
    | { kind: "error"; message: string }
  >({ kind: "idle" });
  const hostRef = useRef<HTMLElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const lockStorageKey = `overlay_locks:${config.project}:${config.env}`;
  const [lockedSelectors, setLockedSelectors] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(lockStorageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((s) => typeof s === "string") : [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(lockStorageKey, JSON.stringify(lockedSelectors));
    } catch {}
  }, [lockStorageKey, lockedSelectors]);

  const toggleLockSelector = (selector: string) => {
    setLockedSelectors((prev) =>
      prev.includes(selector) ? prev.filter((s) => s !== selector) : [...prev, selector],
    );
  };
  const removeLock = (selector: string) => {
    setLockedSelectors((prev) => prev.filter((s) => s !== selector));
  };
  const isLocked = (selector: string) => lockedSelectors.includes(selector);

  const updateAssistant = (
    id: string,
    update: (m: ChatMessage & { role: "assistant" }) => ChatMessage,
  ) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === id && m.role === "assistant" ? update(m) : m)),
    );
  };

  const revertPendingPreviews = () => {
    setMessages((prev) =>
      prev.map((m) => {
        if (
          m.role === "assistant" &&
          m.status.kind === "ready" &&
          !m.status.applied
        ) {
          m.status.revert();
          return {
            ...m,
            status: { kind: "discarded", changeSet: m.status.changeSet },
          };
        }
        return m;
      }),
    );
  };

  const savePatch = async (
    ops: EditOp[],
    source: "chat" | "inspect",
    promptText?: string,
  ): Promise<boolean> => {
    if (ops.length === 0) return true;
    setSaveStatus({ kind: "saving" });
    try {
      const res = await fetch(`${config.apiBase}/api/patches`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${session.token}`,
        },
        body: JSON.stringify({
          pathname: location.pathname,
          ops,
          prompt: promptText,
          source,
        }),
      });
      if (res.status === 401) {
        signOut();
        setSaveStatus({ kind: "error", message: "Session expired" });
        return false;
      }
      if (!res.ok) throw new Error(`API ${res.status}`);
      setSaveStatus({ kind: "saved", at: Date.now() });
      return true;
    } catch (err) {
      setSaveStatus({ kind: "error", message: (err as Error).message });
      return false;
    }
  };

  useEffect(() => {
    hostRef.current = document.getElementById("__overlay_host__");
  }, []);

  useEffect(() => {
    fetch(`${config.apiBase}/api/session`, {
      method: "POST",
      headers: { authorization: `Bearer ${session.token}` },
    })
      .then(async (r) => {
        if (r.status === 401) {
          signOut();
          return null;
        }
        return r.json();
      })
      .then((data) => {
        if (!data) return;
        setSessionId(data.sessionId);
        if (data.limits) {
          setUsage({ used: data.limits.used ?? 0, included: data.limits.included ?? 10 });
        }
      })
      .catch(() => {});
  }, [config.apiBase, session.token, signOut]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, pending]);

  const beginPick = () => {
    if (!hostRef.current) return;
    setPicking(true);
    startPicker({
      ignoreHost: hostRef.current,
      onPick: (el) => {
        setPicking(false);
        setHoverRect(null);
        setSelection(summarizeElement(el));
      },
      onCancel: () => {
        setPicking(false);
        setHoverRect(null);
      },
      onHover: (_el, rect) => setHoverRect(rect),
    });
  };

  const submit = async () => {
    const text = prompt.trim();
    if (!text || pending) return;

    revertPendingPreviews();

    const userId = mkId();
    const asstId = mkId();
    const hadSelection = !!selection;
    setMessages((prev) => [
      ...prev,
      { id: userId, role: "user", text, hadSelection },
      { id: asstId, role: "assistant", prompt: text, status: { kind: "loading" } },
    ]);
    setPrompt("");
    setPending(true);

    try {
      const payload: Record<string, unknown> = { sessionId, prompt: text };
      if (selection) {
        payload.selection = selection;
      } else {
        payload.pageContext = summarizePage(hostRef.current);
      }
      if (lockedSelectors.length > 0) {
        payload.lockedSelectors = lockedSelectors;
      }
      const res = await fetch(`${config.apiBase}/api/prompt`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${session.token}`,
        },
        body: JSON.stringify(payload),
      });
      if (res.status === 401) {
        signOut();
        throw new Error("Session expired — unlock again.");
      }
      if (!res.ok) throw new Error(`API ${res.status}`);
      const data = (await res.json()) as {
        changeSet: ChangeSet;
        usage?: { edits?: number };
      };
      const result = applyOps(data.changeSet.ops as EditOp[]);
      updateAssistant(asstId, (m) => ({
        ...m,
        status: {
          kind: "ready",
          changeSet: data.changeSet,
          revert: result.revert,
          applied: false,
        },
      }));
      setUsage((u) => ({ ...u, used: u.used + (data.usage?.edits ?? 1) }));
    } catch (err) {
      updateAssistant(asstId, (m) => ({
        ...m,
        status: { kind: "error", message: (err as Error).message },
      }));
    } finally {
      setPending(false);
    }
  };

  const keep = async (id: string) => {
    const msg = messages.find((m) => m.id === id);
    if (!msg || msg.role !== "assistant" || msg.status.kind !== "ready") return;
    const status = msg.status;
    updateAssistant(id, (m) => ({
      ...m,
      status: { ...status, applied: true },
    }));
    const ok = await savePatch(
      status.changeSet.ops as EditOp[],
      "chat",
      msg.prompt,
    );
    if (!ok) {
      updateAssistant(id, (m) => ({
        ...m,
        status: { ...status, applied: false },
      }));
    }
  };

  const discard = (id: string) => {
    const msg = messages.find((m) => m.id === id);
    if (!msg || msg.role !== "assistant" || msg.status.kind !== "ready") return;
    msg.status.revert();
    updateAssistant(id, (m) => ({
      ...m,
      status: { kind: "discarded", changeSet: msg.status.kind === "ready" ? msg.status.changeSet : (m.status as { kind: "ready"; changeSet: ChangeSet }).changeSet },
    }));
  };

  const undo = (id: string) => {
    const msg = messages.find((m) => m.id === id);
    if (!msg || msg.role !== "assistant" || msg.status.kind !== "ready") return;
    if (!msg.status.applied) return;
    msg.status.revert();
    updateAssistant(id, (m) => ({
      ...m,
      status: { kind: "discarded", changeSet: (m.status as { kind: "ready"; changeSet: ChangeSet }).changeSet },
    }));
  };

  const clearSelection = () => {
    setSelection(null);
  };

  const pendingOpsCount = messages.reduce(
    (n, m) =>
      m.role === "assistant" && m.status.kind === "ready" && !m.status.applied
        ? n + m.status.changeSet.ops.length
        : n,
    0,
  );

  return (
    <>
      {picking && (
        <>
          <div class="picker-overlay" />
          {hoverRect && (
            <div
              style={{
                position: "fixed",
                left: `${hoverRect.left - 3}px`,
                top: `${hoverRect.top - 3}px`,
                width: `${hoverRect.width + 6}px`,
                height: `${hoverRect.height + 6}px`,
                border: "2px solid #7a66ff",
                borderRadius: "4px",
                boxShadow: "0 0 0 9999px rgba(6,7,12,0.2)",
                pointerEvents: "none",
              }}
            />
          )}
          <div class="picker-hint">Click an element to edit · Esc to cancel</div>
        </>
      )}

      {collapsed && (
        <button class={`launcher-tab dock-${dock}`} onClick={() => setCollapsed(false)} title="Open editor">
          <span class="dot" style={{ width: "10px", height: "10px", borderRadius: "50%", background: "#7a66ff", boxShadow: "0 0 10px #7a66ff" }} />
          <span class="v-label">Overlay</span>
        </button>
      )}

      <aside class={`sidebar dock-${dock}${collapsed ? " collapsed" : ""}`}>
        <div class="sidebar-header">
          <div class="brand">
            <span class="dot" />
            <span>Overlay</span>
          </div>
          <div class="row" style={{ gap: "6px" }}>
            <div class="dock-picker" role="group" aria-label="Dock position">
              {(["left", "top", "bottom", "right"] as Dock[]).map((d) => (
                <button
                  key={d}
                  class={dock === d ? "active" : ""}
                  onClick={() => setDock(d)}
                  title={`Dock ${d}`}
                  aria-label={`Dock ${d}`}
                >
                  {d === "left" ? "⟵" : d === "right" ? "⟶" : d === "top" ? "⇡" : "⇣"}
                </button>
              ))}
            </div>
            <span class="tag">{config.env}</span>
            <button
              class="btn ghost"
              onClick={() => setCollapsed(true)}
              title="Collapse"
              aria-label="Collapse"
            >
              ‹
            </button>
          </div>
        </div>

        <div class="tabs">
          <button
            class={tab === "chat" ? "tab active" : "tab"}
            onClick={() => setTab("chat")}
          >
            Chat
            {pendingOpsCount > 0 && <span class="count">{pendingOpsCount}</span>}
          </button>
          <button
            class={tab === "inspect" ? "tab active" : "tab"}
            onClick={() => setTab("inspect")}
            disabled={!selection}
            style={!selection ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
          >
            Inspect
          </button>
          <button
            class={tab === "code" ? "tab active" : "tab"}
            onClick={() => setTab("code")}
            disabled={!selection}
            style={!selection ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
          >
            Code
          </button>
        </div>

        {tab === "chat" ? (
          <ChatPane
            selection={selection}
            beginPick={beginPick}
            clearSelection={clearSelection}
            messages={messages}
            pending={pending}
            prompt={prompt}
            setPrompt={setPrompt}
            onSubmit={submit}
            onKeep={keep}
            onDiscard={discard}
            onUndo={undo}
            messagesEndRef={messagesEndRef}
            lockedSelectors={lockedSelectors}
            isLocked={selection ? isLocked(selection.selector) : false}
            onToggleLock={() => selection && toggleLockSelector(selection.selector)}
            onRemoveLock={removeLock}
          />
        ) : !selection ? (
          <div class="sidebar-body">
            <div class="empty-state">
              <div class="icon">◎</div>
              <div>Pick an element to use this tab.</div>
              <button class="pick-button" onClick={beginPick} style={{ marginTop: "6px" }}>
                <span class="dot" /> Pick element
              </button>
            </div>
          </div>
        ) : tab === "inspect" ? (
          <div class="inspect-pane">
            <div class="inspect-context">
              <div class="selection-card">
                <div class="row" style={{ justifyContent: "space-between" }}>
                  <span class="muted">Selected · &lt;{selection.tag}&gt;</span>
                  <div class="row" style={{ gap: "4px" }}>
                    <button class="btn ghost" onClick={beginPick}>
                      Pick another
                    </button>
                    <button class="btn ghost" onClick={clearSelection} aria-label="Clear">
                      ✕
                    </button>
                  </div>
                </div>
                <div class="sel" style={{ marginTop: "6px" }}>{selection.selector}</div>
                {selection.text && <div class="preview">{selection.text}</div>}
              </div>
            </div>
            <div class="inspect-scroll">
              <InspectTab
                selection={selection}
                apiBase={config.apiBase}
                token={session.token}
                projectId={config.project}
                onKeep={async (ops) => {
                  if (ops.length === 0) return;
                  const ok = await savePatch(ops, "inspect");
                  if (ok) setUsage((u) => ({ ...u, used: u.used + 1 }));
                }}
              />
            </div>
          </div>
        ) : (
          <CodeTab
            selection={selection}
            beginPick={beginPick}
            clearSelection={clearSelection}
            onApply={async (ops) => {
              if (ops.length === 0) return;
              const ok = await savePatch(ops, "inspect");
              if (ok) setUsage((u) => ({ ...u, used: u.used + 1 }));
            }}
          />
        )}

        <div class="footer-bar">
          <span>
            {config.project} · {config.env}
            {saveStatus.kind === "saving" && <> · <span class="muted">saving…</span></>}
            {saveStatus.kind === "saved" && <> · <span class="muted">saved ✓</span></>}
            {saveStatus.kind === "error" && (
              <> · <span class="error">{saveStatus.message}</span></>
            )}
          </span>
          <div class="row" style={{ gap: "8px" }}>
            <span>{usage.used} / {usage.included} edits</span>
            <button
              class="btn ghost"
              onClick={() => setCollapsed(true)}
              title="Hide sidebar (session stays active)"
            >
              Hide
            </button>
            <button
              class="btn ghost"
              onClick={signOut}
              title="Sign out — will require the unlock key to re-open"
            >
              Sign out
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

function ChatPane({
  selection,
  beginPick,
  clearSelection,
  messages,
  pending,
  prompt,
  setPrompt,
  onSubmit,
  onKeep,
  onDiscard,
  onUndo,
  messagesEndRef,
  lockedSelectors,
  isLocked,
  onToggleLock,
  onRemoveLock,
}: {
  selection: Selection | null;
  beginPick: () => void;
  clearSelection: () => void;
  messages: ChatMessage[];
  pending: boolean;
  prompt: string;
  setPrompt: (v: string) => void;
  onSubmit: () => void;
  onKeep: (id: string) => void;
  onDiscard: (id: string) => void;
  onUndo: (id: string) => void;
  messagesEndRef: { current: HTMLDivElement | null };
  lockedSelectors: string[];
  isLocked: boolean;
  onToggleLock: () => void;
  onRemoveLock: (selector: string) => void;
}) {
  const placeholder = selection
    ? `Message about <${selection.tag}>…`
    : "Message about this page…";

  return (
    <div class="chat-pane">
      <div class="chat-context">
        {selection ? (
          <div class={isLocked ? "context-pill locked" : "context-pill"}>
            <span class="muted">{isLocked ? "🔒 Locked" : "Targeting"}</span>
            <span class="sel">{selection.selector}</span>
            <div class="spacer" />
            <button
              class="btn ghost"
              onClick={onToggleLock}
              title={isLocked ? "Unlock — prompts may edit this" : "Lock — prompts will skip this"}
            >
              {isLocked ? "Unlock" : "Lock"}
            </button>
            <button class="btn ghost" onClick={beginPick}>Pick another</button>
            <button class="btn ghost" onClick={clearSelection} aria-label="Clear target">✕</button>
          </div>
        ) : (
          <div class="context-pill">
            <span class="muted">Page-wide · no element targeted</span>
            <div class="spacer" />
            <button class="btn ghost" onClick={beginPick}>Pick element</button>
          </div>
        )}
      </div>

      {lockedSelectors.length > 0 && (
        <div class="lock-list">
          <div class="head">🔒 Locked · {lockedSelectors.length}</div>
          {lockedSelectors.map((sel) => (
            <div class="item" key={sel}>
              <span class="sel">{sel}</span>
              <button
                class="btn ghost"
                onClick={() => onRemoveLock(sel)}
                title="Remove lock"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      <div class="chat-messages">
        {messages.length === 0 && (
          <div class="empty-state" style={{ padding: "20px 8px" }}>
            <div class="icon">✶</div>
            <div>Ask for a change. Keep what works, discard what doesn't.</div>
          </div>
        )}
        {messages.map((m) =>
          m.role === "user" ? (
            <div key={m.id} class="msg user">
              <div class="bubble">{m.text}</div>
              <div class="meta">
                {m.hadSelection ? "targeted element" : "page-wide"}
              </div>
            </div>
          ) : (
            <AssistantMessage
              key={m.id}
              message={m}
              onKeep={() => onKeep(m.id)}
              onDiscard={() => onDiscard(m.id)}
              onUndo={() => onUndo(m.id)}
            />
          ),
        )}
        <div ref={messagesEndRef} />
      </div>

      <div class="chat-composer">
        <textarea
          class="prompt"
          placeholder={placeholder}
          value={prompt}
          onInput={(e) => setPrompt((e.target as HTMLTextAreaElement).value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) onSubmit();
          }}
          disabled={pending}
        />
        <div class="row">
          <span class="muted" style={{ fontSize: "11px" }}>
            {selection ? "⌘↵ to send" : "Page-wide · ⌘↵ to send"}
          </span>
          <div class="spacer" />
          <button
            class="btn accent"
            onClick={onSubmit}
            disabled={!prompt.trim() || pending}
          >
            {pending ? "Thinking…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AssistantMessage({
  message,
  onKeep,
  onDiscard,
  onUndo,
}: {
  message: ChatMessage & { role: "assistant" };
  onKeep: () => void;
  onDiscard: () => void;
  onUndo: () => void;
}) {
  const { status } = message;
  if (status.kind === "loading") {
    return (
      <div class="msg assistant">
        <div class="bubble">
          <div class="status"><span class="pulse" /> Planning ops…</div>
        </div>
      </div>
    );
  }
  if (status.kind === "error") {
    return (
      <div class="msg assistant">
        <div class="bubble">
          <div class="error">{status.message}</div>
        </div>
      </div>
    );
  }
  if (status.kind === "discarded") {
    return (
      <div class="msg assistant">
        <div class="bubble" style={{ opacity: 0.65 }}>
          <div class="muted" style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.08em" }}>
            {status.changeSet.ops.length} ops · discarded
          </div>
        </div>
      </div>
    );
  }
  // ready
  const { changeSet, applied } = status;
  const hasOps = changeSet.ops.length > 0;
  return (
    <div class="msg assistant">
      <div class="bubble" style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {hasOps && (
          <div class="row" style={{ gap: "6px" }}>
            <span class="muted" style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.08em" }}>
              {changeSet.ops.length} op{changeSet.ops.length === 1 ? "" : "s"}
            </span>
            {applied && <span class="tag" style={{ color: "#9b8cff" }}>applied</span>}
          </div>
        )}
        {changeSet.rationale && (
          <div style={{ fontSize: "12px", color: "#d3d8e1" }}>{changeSet.rationale}</div>
        )}
        {changeSet.ops.map((op) => (
          <OpCard key={op.id} op={op} />
        ))}
        {hasOps && (
          applied ? (
            <div class="row">
              <div class="spacer" />
              <button class="btn" onClick={onUndo}>Undo</button>
            </div>
          ) : (
            <div class="row">
              <button class="btn" onClick={onDiscard}>Discard</button>
              <div class="spacer" />
              <button class="btn primary" onClick={onKeep}>Keep</button>
            </div>
          )
        )}
      </div>
    </div>
  );
}

function OpCard({ op }: { op: EditOp }) {
  return (
    <div class="op">
      <div class="type">{op.type}</div>
      {(op.type === "text.set" || op.type === "text.rewrite") && (
        <div class="diff" style={{ marginTop: "4px" }}>
          {op.before && <div class="before">{op.before}</div>}
          <div class="after">{op.after}</div>
        </div>
      )}
      {op.type === "style.set" && (
        <div style={{ marginTop: "4px", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: "11px" }}>
          {Object.entries(op.declarations).map(([k, v]) => (
            <div key={k}><span style={{ color: "#9b8cff" }}>{k}</span>: {v};</div>
          ))}
        </div>
      )}
      {(op.type === "class.add" || op.type === "class.remove") && (
        <div style={{ marginTop: "4px" }}>
          {op.type === "class.add" ? "+" : "−"} .{op.className}
        </div>
      )}
      {op.type === "asset.replace" && (
        <div class="diff" style={{ marginTop: "4px" }}>
          {op.before && <div class="before">{op.before}</div>}
          <div class="after">{op.after}</div>
        </div>
      )}
      {op.type === "attr.set" && (
        <div style={{ marginTop: "4px" }}>
          {op.name} = "{op.value}"
        </div>
      )}
      {op.type === "attr.remove" && (
        <div style={{ marginTop: "4px" }}>− [{op.name}]</div>
      )}
      {op.type === "element.remove" && (
        <div style={{ marginTop: "4px" }}>− {op.target.selector}</div>
      )}
      {op.type === "element.insert" && (
        <div style={{ marginTop: "4px", fontSize: "11px", color: "#d3d8e1" }}>
          insert {op.position ?? "after"} {op.target.selector}
        </div>
      )}
      {op.type === "html.replace" && (
        <div style={{ marginTop: "4px", fontSize: "11px", color: "#d3d8e1", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", whiteSpace: "pre-wrap", wordBreak: "break-all", maxHeight: "120px", overflow: "auto" }}>
          {op.html.slice(0, 600)}{op.html.length > 600 ? "…" : ""}
        </div>
      )}
      {op.rationale && <div class="rationale">{op.rationale}</div>}
    </div>
  );
}
